import geolib from "geolib";
import i18n from "../i18n";
import { keys } from "../i18n";
import { LOCATION_TYPE } from "../app-constants";

export class ValidationError extends Error {
  constructor(validationResult) {
    super("Validation failed");
    this.name = "ValidationError";
    this.message = validationResult;
  }
}

export class ValidationErrorItem {
  constructor(message, locationCode = null, featureIndex = null) {
    this.message = message;
    this.locationCode = locationCode;
    this.featureIndex = featureIndex;
  }
}

const createUnsupportedGeometryError = (geometryType, locationCode) => {
  const baseMessage = `${i18n.t(keys.GEOJSON_ERROR_UNSUPPORTED_GEOMETRY_TYPE)}: ${geometryType}`;
  return locationCode ? `${baseMessage} - ${locationCode}` : baseMessage;
};

const findDuplicateLocations = (locations) => {
  const counts = {};

  // Count occurrences
  locations.forEach((location) => {
    const code = location.locationCode;
    counts[code] = (counts[code] || 0) + 1;
  });

  // Return all codes that appear more than once
  return Object.keys(counts).filter((code) => counts[code] > 1);
};

/**
 * Convert latitude/longitude difference to meters.
 */
const calculateDistanceInMeters = (lat1, lon1, lat2, lon2) => {
  // get distance between two points in meters
  const distanceInMeters = geolib.getDistance(
    { latitude: lat1, longitude: lon1 },
    { latitude: lat2, longitude: lon2 },
    1, // accuracy
    13 // number of digits after decimal point
  );

  // get directional values using bearing calculation
  const bearing = geolib.getBearing(
    { latitude: lat1, longitude: lon1 },
    { latitude: lat2, longitude: lon2 }
  );

  // convert polar (distance, bearing) to cartesian (x, y)
  const radians = (bearing * Math.PI) / 180;
  const x = Math.sin(radians) * distanceInMeters;
  const y = Math.cos(radians) * distanceInMeters;

  return { x, y };
};

const transformGeoVerticesToMeters = (geoVertices, anchorPoint) => {
  return geoVertices.map((vertex) => {
    const distance = calculateDistanceInMeters(
      anchorPoint.latitude,
      anchorPoint.longitude, // anchor latitude, longitude
      vertex.y,
      vertex.x // point latitude, longitude
    );

    return {
      x: distance.x,
      y: distance.y,
    };
  });
};

/**
 * Validate polygon point count for a given location type.
 */
export const validatePolygonForLocationType = (polygon, locationType) => {
  const pointCount = polygon ? polygon.length : 0;

  // parking slips and dock doors must be rectangular (4 points) - these are "trailer locations"
  if (locationType === LOCATION_TYPE.PARKING_SLIP || locationType === LOCATION_TYPE.DOCK_DOOR) {
    if (pointCount !== 4) {
      const locationTypeKey =
        locationType === LOCATION_TYPE.PARKING_SLIP
          ? keys.LOCATION_TYPE_PARKING_SLIPS
          : keys.LOCATION_TYPE_DOCK_DOORS;
      const translatedLocationType = i18n.t(locationTypeKey);
      throw new Error(
        i18n.t(keys.GEOJSON_ERROR_TRAILER_LOCATION_POINTS, { locationType: translatedLocationType })
      );
    }
  } else {
    // gates and no-go areas can have any number of points >= 3
    if (pointCount < 3) {
      throw new Error(i18n.t(keys.GEOJSON_ERROR_GATE_MIN_POINTS));
    }
  }
};

/**
 * Validate and clean polygon coordinates.
 */
const validateAndCleanPolygon = (coordinates, locationCode, locationType) => {
  const polygonErrors = [];

  // check if polygon is closed (first = last)
  const isClosedPolygon =
    coordinates[0][0] === coordinates[coordinates.length - 1][0] &&
    coordinates[0][1] === coordinates[coordinates.length - 1][1];

  // if the polygon is closed, remove the closing point to avoid duplication
  // if it's not closed, keep all points as they are
  const cleanedCoordinates = isClosedPolygon ? coordinates.slice(0, -1) : coordinates;

  // validate using the shared validation function
  try {
    validatePolygonForLocationType(cleanedCoordinates, locationType);
  } catch (error) {
    polygonErrors.push(new ValidationErrorItem(`${error.message} - ${locationCode}`, locationCode));
  }

  return { cleanedCoordinates, polygonErrors };
};

const transformGeoJsonFeaturesToLocations = (features, anchorPoint) => {
  const featureErrors = [];
  const locations = [];
  features
    .filter(
      (feature) =>
        feature.properties?.locationType === LOCATION_TYPE.DOCK_DOOR ||
        feature.properties?.locationType === LOCATION_TYPE.PARKING_SLIP ||
        feature.properties?.locationType === LOCATION_TYPE.GATE
    )
    // filtering out features that we don't support yet
    .filter((feature) => feature.geometry.type !== "Point")
    .forEach((feature, index) => {
      // validate required properties
      const locationCode = feature.properties?.locationCode?.trim();
      if (!locationCode) {
        featureErrors.push(
          new ValidationErrorItem(i18n.t(keys.GEOJSON_ERROR_MISSING_LOCATION_CODE), null, index)
        );
        return;
      }

      // extract coordinates based on geometry type
      let rawCoordinates;
      if (feature.geometry.type === "MultiPolygon") {
        rawCoordinates = feature.geometry.coordinates[0][0];
      } else if (feature.geometry.type === "Polygon") {
        rawCoordinates = feature.geometry.coordinates[0];
      } else {
        featureErrors.push(
          new ValidationErrorItem(
            createUnsupportedGeometryError(feature.geometry.type, locationCode)
          )
        );
        return;
      }
      const { cleanedCoordinates, polygonErrors } = validateAndCleanPolygon(
        rawCoordinates,
        locationCode,
        feature.properties.locationType
      );
      polygonErrors.forEach((polygonError) => {
        featureErrors.push(polygonError);
      });

      // transform coordinates to our Point format
      const geoVertices = cleanedCoordinates.map((coordinate) => ({
        x: coordinate[0],
        y: coordinate[1],
      }));

      // transform to meters
      const polygon = transformGeoVerticesToMeters(geoVertices, anchorPoint);

      const location = {
        locationCode: locationCode,
        locationType: feature.properties.locationType,
        polygon: polygon,
      };

      // only include parkingDirection for DOCK_DOOR and PARKING_SLIP
      if (
        feature.properties.locationType === LOCATION_TYPE.DOCK_DOOR ||
        feature.properties.locationType === LOCATION_TYPE.PARKING_SLIP
      ) {
        const direction = feature.properties.parkingDirection;
        if (typeof direction === "string") {
          location.parkingDirection = parseFloat(direction) || 0;
        } else {
          location.parkingDirection = direction || 0;
        }
      }

      locations.push(location);
    });

  const duplicates = findDuplicateLocations(locations);
  if (duplicates.length > 0) {
    featureErrors.push(
      new ValidationErrorItem(
        `${i18n.t(keys.GEOJSON_ERROR_DUPLICATE_LOCATION_CODES)}: ${duplicates.join(", ")}`
      )
    );
  }

  return { locations, featureErrors };
};

/**
 * Process GeoJSON file.
 */
export const processGeoJSONFile = async (fileContent) => {
  const errors = [];
  let jsonData;

  try {
    jsonData = JSON.parse(fileContent);
  } catch (parseError) {
    errors.push(
      new ValidationErrorItem(`${i18n.t(keys.ERROR_INVALID_JSON)}: ${parseError.message}`)
    );
    throw new ValidationError({ errors });
  }

  if (jsonData && !jsonData.features?.length) {
    errors.push(new ValidationErrorItem(i18n.t(keys.GEOJSON_ERROR_NO_FEATURES)));
    throw new ValidationError({ errors });
  }

  const yardFeature = jsonData.features[0];

  // extract anchor point based on geometry type
  let anchorPoint;
  if (yardFeature.geometry.type === "MultiPolygon") {
    anchorPoint = {
      longitude: yardFeature.geometry.coordinates[0][0][0][0],
      latitude: yardFeature.geometry.coordinates[0][0][0][1],
    };
  } else if (yardFeature.geometry.type === "Polygon") {
    anchorPoint = {
      longitude: yardFeature.geometry.coordinates[0][0][0],
      latitude: yardFeature.geometry.coordinates[0][0][1],
    };
  } else {
    const locationCode = yardFeature.properties?.locationCode;
    errors.push(
      new ValidationErrorItem(
        createUnsupportedGeometryError(yardFeature.geometry.type, locationCode)
      )
    );
  }

  const { locations, featureErrors } = transformGeoJsonFeaturesToLocations(
    jsonData.features,
    anchorPoint
  );
  featureErrors.forEach((featureError) => {
    errors.push(featureError);
  });

  // check if we have any valid locations after processing
  if (locations.length === 0 && errors.length === 0) {
    errors.push(new ValidationErrorItem(i18n.t(keys.GEOJSON_ERROR_NO_VALID_LOCATIONS)));
  }

  if (errors.length > 0) {
    throw new ValidationError({ errors });
  }

  return {
    anchorPoint,
    locations,
  };
};
